import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import {
  CompareHeader,
  SavingsBanner,
  UnitsControl,
} from "./pricing-calc-shell";
import type { CalculatorState } from "./pricing-calculator";
import { LeftCardBody, RightCardBody } from "./pricing-compare-cards";
import { CostCards } from "./pricing-cost-cards";
import { DesktopHero } from "./pricing-desktop-hero";
import { DesktopIncluded } from "./pricing-desktop-included";
import type { InfoId } from "./pricing-popovers";
import { HEADING_RICH_TAGS, T, hiddenCostsLead } from "./pricing-shared";

function SectionHeading({
  title,
  lead,
  size,
}: {
  readonly title: ReactNode;
  readonly lead: ReactNode;
  readonly size: number;
}): ReactNode {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-end",
        gap: 32,
        flexWrap: "wrap",
        margin: "8px 0 26px",
      }}
    >
      <h2
        style={{
          fontFamily: T.display,
          fontWeight: 600,
          fontSize: size,
          lineHeight: 1.04,
          letterSpacing: "-0.02em",
          color: T.ink,
          margin: 0,
          maxWidth: 560,
        }}
      >
        {title}
      </h2>
      <p
        style={{
          fontFamily: T.sans,
          fontSize: 16,
          lineHeight: 1.55,
          color: "#575B63",
          margin: "0 0 5px auto",
          maxWidth: 480,
        }}
      >
        {lead}
      </p>
    </div>
  );
}

/** Desktop pricing layout (fixed wide), composed from section parts. */
export function PricingDesktop({
  calc,
  openInfo,
  onToggle,
  onClose,
}: {
  readonly calc: CalculatorState;
  readonly openInfo: InfoId | null;
  readonly onToggle: (id: InfoId) => void;
  readonly onClose: () => void;
}): ReactNode {
  const tPricingShared = useTranslations("public.pricingShared");
  const t = useTranslations("public.pricingDesktop");
  return (
    <div
      style={{
        fontFamily: T.sans,
        background: "#FFFFFF",
        color: T.ink,
        overflowX: "hidden",
      }}
    >
      <DesktopHero />
      <DesktopIncluded />

      <div
        // i18n-exempt: data attribute, never rendered — section marker for the screenshot tooling
        data-screen-label="Verbrauch"
        style={{ borderTop: "1px solid rgba(11,15,25,0.10)" }}
      >
        <div
          style={{
            maxWidth: 1180,
            margin: "0 auto",
            padding: "40px 28px 92px",
          }}
        >
          <SectionHeading
            title={t.rich("keineVerstecktenKosten", HEADING_RICH_TAGS)}
            size={42}
            lead={hiddenCostsLead(tPricingShared)}
          />
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}
          >
            <CostCards
              compact={false}
              openInfo={openInfo}
              onToggle={onToggle}
              onClose={onClose}
            />
          </div>
        </div>
      </div>

      <div
        id="spar-rechner"
        // i18n-exempt: data attribute, never rendered — section marker for the screenshot tooling
        data-screen-label="Rechner"
        style={{ borderTop: "1px solid rgba(11,15,25,0.10)" }}
      >
        <div
          style={{
            maxWidth: 1180,
            margin: "0 auto",
            padding: "40px 28px 100px",
          }}
        >
          <SectionHeading
            title={t.rich("rechneDeinenVorteilAus", HEADING_RICH_TAGS)}
            size={42}
            lead={
              <>
                {t("tragHeutigenToolKosten")}{" "}
                <strong style={{ color: T.ink, fontWeight: 600 }}>
                  {t("alleBetraegeProMonat")}
                </strong>
              </>
            }
          />
          <UnitsControl calc={calc} compact={false} />
          <SavingsBanner calc={calc} compact={false} />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 20,
              alignItems: "start",
            }}
          >
            <div
              style={{
                border: "1px solid rgba(11,15,25,0.12)",
                borderRadius: 16,
                background: "#fff",
              }}
            >
              <CompareHeader
                eyebrow={t("hausverwalterAi")}
                eyebrowColor="#9AA0A6"
                title={t("heutigenTools")}
                total={calc.leftTotalLabel}
                totalUnit={t("monat")}
                totalUnitColor="#8A8D94"
                yearLabel={calc.leftYearLabel}
                compact={false}
              />
              <div style={{ padding: "0 22px 14px" }}>
                <LeftCardBody
                  calc={calc}
                  openInfo={openInfo}
                  onToggle={onToggle}
                  onClose={onClose}
                />
              </div>
            </div>
            <div
              style={{
                position: "relative",
                border: "1px solid rgba(0,128,112,0.25)",
                borderRadius: 16,
                background: "linear-gradient(180deg, #FAFAFA 0%, #ffffff 55%)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: 0,
                  height: 3,
                  background: T.accent,
                }}
              />
              <CompareHeader
                eyebrow={t("hausverwalterAi2")}
                eyebrowColor={T.accent}
                title={t("systemPreis")}
                total={calc.rightTotalLabel}
                totalUnit={t("monat")}
                totalUnitColor={T.accent}
                yearLabel={calc.rightYearLabel}
                compact={false}
              />
              <div style={{ padding: "2px 22px 18px" }}>
                <RightCardBody calc={calc} compact={false} />
              </div>
            </div>
          </div>
          <p
            style={{
              fontFamily: T.sans,
              fontSize: 13,
              lineHeight: 1.5,
              color: "#8A8D94",
              margin: "16px 2px 0",
              textAlign: "center",
            }}
          >
            {t("richtwerteEchtenHausverwaltungenBasis")}
          </p>
        </div>
      </div>
    </div>
  );
}
